import type {TabsProps} from 'antd/lib/tabs';
import {
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    Drawer,
    FloatButton,
    Form,
    Input,
    message,
    Pagination,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCircleLeft,
    faMoneyBillTrendUp,
    faSackDollar,
    faUser,
    faUsers,
    faUserTie
} from "@fortawesome/free-solid-svg-icons";
import {EyeOutlined, ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import {useEffect, useState} from "react";
import {COMMON_DROPDOWN, IDENTIFICATION_SOURCE, PARAMETER_LIST} from "../../../util/Constants";
import adminService from "../../../service/adminService";
import {
    MSG_FAILED_TO_FETCH_FUND_LIST_ERROR,
    MSG_FAILED_TO_FETCH_IDENTIFICATION_SOURCE_TYPES_ERROR,
    MSG_FETCH_PORTFOLIO_WISE_CUSTOMER_SUMMARY_ERROR,
    MSG_FETCH_RELATIONSHIP_MANAGER_SUMMARY_ERROR,
    MSG_FETCH_RELATIONSHIP_MANAGER_WISE_CUSTOMER_LIST_ERROR
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import {RelationshipManagerSummaryDto} from "../../../dto/admin/RelationshipManagerSummaryDto";
import CommonFunction from "../../../util/CommonFunction";
import {FundWiseCustomerSummaryDto} from "../../../dto/admin/FundWiseCustomerSummaryDto";
import TableSearchComp from "../../../components/common/tableSearch/TableSearchComp";
import {DropdownListDto} from "../../../dto/DropdownListDto";
import {InputDropdownParameterDto} from "../../../dto/InputDropdownParameterDto";
import customerService from "../../../service/customerService";
import {CustomerDto} from "../../../dto/CustomerDto";
import {AddressDto} from "../../../dto/AddressDto";
import PendingChangeRequest from "../../../components/customerDashboard/pendingChangeRequest";
import RecentTransaction from "../../../components/rmDashboard/recentTransaction";
import {isRelationshipManagerUser} from "../../../security/KeyclockConfig";
import {useHistory} from "react-router-dom";
import {CustomerFilterDto} from "../../../dto/admin/CustomerFilterDto";
import Validation from "../../../util/Validation";

const {Panel} = Collapse;
const reportsUrl: string | undefined = process.env.NDB_REPORT_MODULE_URL ? process.env.NDB_REPORT_MODULE_URL : "";
const RMDashboardPage = (props: any) => {
        const [open, setOpen] = useState(false);
        const [relationshipManagerSummary, setRelationshipManagerSummary] = useState<RelationshipManagerSummaryDto>();
        const [portfolioWiseCustomerList, setPortfolioWiseCustomerList] = useState<FundWiseCustomerSummaryDto[]>();
        const [rmWiseCustomerList, setRmWiseCustomerList] = useState<CustomerDto[]>();
        const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto>();
        const [isSummaryLoading, setSummaryLoading] = useState(false);
        const [isCustomerTableLoading, setCustomerTableLoading] = useState(false);
        const [isPortfolioWiseTableLoading, setPortfolioWiseTableLoading] = useState(false);
        const [relationshipManagerId, setRelationshipManagerId] = useState(0);
        const [fundDropdownList, setFundDropdownList] = useState<DropdownListDto[]>([]);
        const [portfolioNumberList, setPortfolioNumberList] = useState<DropdownListDto[]>([]);
        const [portfolioNumber, setPortfolioNumber] = useState<string>();
        const [isPortfolioNumbersLoading, setPortfolioNumbersLoading] = useState(false);
        const [accountNumber, setAccountNumber] = useState<string>("")
        const [identificationSourceTypeList, setIdentificationSourceTypeList] = useState<DropdownListDto[]>([]);

        const history = useHistory();
        const [pagination, setPagination] = useState({
            current: 1,
            pageSize: 10,
            total: 10,
        });

        const [filter, setFilter] = useState<CustomerFilterDto>({
            rmId: 0,
            identificationSource: null,
            identificationValue: null,
            customerName: null,
            cin: null,
            page: 1,
            size: 10,
            sort: ['createdDate', 'desc']
        });
        const [form] = Form.useForm();

        useEffect(() => {
            const rmCode = localStorage.getItem("rmCode");

            fetchRelationshipManagerSummary(rmCode)
            getIdentificationSourceTypes();
        }, [])

        const extractFirstPart = (str: string) => {
            return str.split(' - ')[0];
        }
        const fetchRelationshipManagerSummary = (rmCode: string | null) => {
            if (null != rmCode) {
                setSummaryLoading(true)
                adminService.fetchRelationshipManagerSummery(rmCode).then(res => {
                    if (res.data !== null) {
                        let results = res.data.results;
                        setRelationshipManagerSummary(results)
                        setRelationshipManagerId(results.relationshipManagerId)
                        let filterDto: CustomerFilterDto = {
                            rmId: results.relationshipManagerId,
                            page: 1,
                            size: pagination.pageSize,
                            sort: filter.sort
                        }
                        setFilter(filterDto)
                        fetchRMWiseCustomers(1, 10, filterDto)
                        setRelationshipManagerId(results.relationshipManagerId)
                        setSummaryLoading(false)
                    } else {
                        message.error(MSG_FETCH_RELATIONSHIP_MANAGER_SUMMARY_ERROR).then();
                        setSummaryLoading(false)

                    }
                }, (reason) => {
                    ErrorHandler.handleError(reason);
                    setSummaryLoading(false)

                })
            }
        }

        const handlePaginationChange = (page: number, pageSize: number) => {
            fetchRMWiseCustomers(page, pageSize, filter);
        };
        const fetchRMWiseCustomers = (page: number, pageSize: number, filterDto: CustomerFilterDto) => {
            setCustomerTableLoading(true)

            filterDto.page = page - 1;
            filterDto.size = pageSize;

            adminService.fetchRelationshipManagerWiseCustomerList(filterDto).then(res => {
                if (res.data !== null) {
                    setRmWiseCustomerList(res.data.results.customerMstDtos)
                    setPagination({
                        current: res.data.results.page + 1, // Adjusting to be 1-based index
                        pageSize: res.data.results.size,
                        total: res.data.results.allCount,
                    });
                    setCustomerTableLoading(false)
                } else {
                    message.error(MSG_FETCH_RELATIONSHIP_MANAGER_WISE_CUSTOMER_LIST_ERROR).then();
                    setCustomerTableLoading(false)

                }
            }, (reason) => {
                ErrorHandler.handleError(reason);
                setCustomerTableLoading(false)

            })
        }
        const fetchPortfolioWiseCustomerSummary = (fundId: number | null) => {
            setPortfolioWiseTableLoading(true)
            adminService.fetchCustomerWiseSummaryList(fundId, relationshipManagerId).then(res => {
                if (res.data !== null) {
                    setPortfolioWiseCustomerList(res.data.results)
                    loadFundList()
                    setPortfolioWiseTableLoading(false)
                } else {
                    message.error(MSG_FETCH_PORTFOLIO_WISE_CUSTOMER_SUMMARY_ERROR).then();
                    setPortfolioWiseTableLoading(false)

                }
            }, (reason) => {
                ErrorHandler.handleError(reason);
                setPortfolioWiseTableLoading(false)

            })
        }

        const loadFundList = () => {
            const inputDropdownParameterDto: InputDropdownParameterDto = {
                dropdownDataSourceId: COMMON_DROPDOWN.FUND_LIST,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            }
            customerService.getCommonDropdown(inputDropdownParameterDto).then(res => {
                if (res.data !== null) {
                    setFundDropdownList(res.data.results.activeItemList)
                } else {
                    message.error(MSG_FAILED_TO_FETCH_FUND_LIST_ERROR).then();
                }
            }, (reason) => {
                ErrorHandler.handleError(reason);

            })
        }
        const showDrawer = (data: CustomerDto) => {
            fetchCustomerPortfolioNumbers(data.id as number)
            setOpen(true);
            setSelectedCustomer(data)
        };

        const onClose = () => {
            setSelectedCustomer(undefined)
            setOpen(false);
        };
        const getIdentificationSourceTypes = () => {
            const inputDropdownParameterDto: InputDropdownParameterDto = {
                dropdownDataSourceId: COMMON_DROPDOWN.IDENTIFICATION_SOURCE,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            }
            customerService.getCommonDropdown(inputDropdownParameterDto).then(res => {
                if (res.data !== null) {
                    let activeItemList = res.data.results.activeItemList;
                    setIdentificationSourceTypeList(activeItemList)
                } else {
                    message.error(MSG_FAILED_TO_FETCH_IDENTIFICATION_SOURCE_TYPES_ERROR).then();
                }
            }, (reason) => {
                ErrorHandler.handleError(reason);
            })
        }
        const portfolioWiseCustomerColumns = [
            {
                title: 'Name of Customer',
                dataIndex: 'customerName',
                key: 'customerName',
                sorter: (a: any, b: any) => {
                    a = a.customerName == null ? '' : a.customerName
                    b = b.customerName == null ? '' : b.customerName
                    return a.localeCompare(b);
                },
                ...TableSearchComp('customerName', 'Name of Customer'),
                render: (text: any) => (
                    CommonFunction.validateText(text)
                ),
            },
            {
                title: 'Portfolio No',
                dataIndex: 'portfolioNumber',
                key: 'portfolioNumber',
                sorter: (a: any, b: any) => {
                    a = a.portfolioNumber == null ? '' : a.portfolioNumber
                    b = b.portfolioNumber == null ? '' : b.portfolioNumber
                    return a.localeCompare(b);
                },
                ...TableSearchComp('portfolioNumber', 'Portfolio No'),
                render: (text: any) => (
                    CommonFunction.validateText(text)
                ),
            },

            {
                title: 'Fund Name',
                dataIndex: 'fundName',
                key: 'fundName',
                sorter: (a: any, b: any) => {
                    a = a.fundName == null ? '' : a.fundName
                    b = b.fundName == null ? '' : b.fundName
                    return a.localeCompare(b);
                },
                ...TableSearchComp('fundName', 'Fund Name'),
                render: (text: any) => (
                    CommonFunction.validateText(text)
                ),
            },
            {
                title: 'Value',
                dataIndex: 'fundValue',
                key: 'fundValue',
                align: "right",
                sorter: (a: any, b: any) => (a.fundValue - b.fundValue),
                ...TableSearchComp('fundValue', 'Value'),
                render: (text: any) => (
                    CommonFunction.decimalFormat(text)
                ),
            }
        ];

        const customerDetailsColumns = [
            {
                title: 'Name of Customer',
                dataIndex: 'nameToAppearOnStatement',
                key: 'nameToAppearOnStatement',
                render: (text: any) => {
                    return <pre style={{ display: 'inline' }}>{CommonFunction.validateText(text)}</pre>
                },
            },
            {
                title: 'CIN',
                dataIndex: 'cin',
                key: 'cin',
                render: (text: any) => (
                    CommonFunction.validateText(text)
                ),
            },
            {
                title: 'Identification Type',
                dataIndex: 'identificationSource',
                key: 'identificationSource',
                align: 'center',
                render: (text: any, rec: any) => {
                    let sourceType = text.sourceType;
                    return (
                        <Tag className={sourceType.toUpperCase() == 'DRIVING LICENSE' ? 'drl-tbl-tag' : 'tbl-tag'}
                             color={getColor(sourceType)} key={sourceType}>
                            {sourceType.toUpperCase()}
                        </Tag>
                    )
                }
            },
            {
                title: 'Identification Value',
                dataIndex: 'identificationValue',
                key: 'identificationValue',
                render: (text: any) => (
                    CommonFunction.validateText(text)
                ),
            },
            {
                title: 'Action',
                key: 'action',
                align: "center",
                fixed: 'right' as const,
                render: (rec: CustomerDto) => (
                    <Space size="middle">
                        <Button className='btn-action-outline-common btn-action-view ' onClick={() => showDrawer(rec)}
                                icon={<EyeOutlined/>}>View</Button>
                    </Space>
                ),
            },
        ];


        const getColor = (identificationSourceType: string | undefined) => {
            if (undefined != identificationSourceType) {
                switch (identificationSourceType) {
                    case IDENTIFICATION_SOURCE.NIC: {
                        return 'green';
                    }
                    case IDENTIFICATION_SOURCE.PP: {
                        return 'geekblue';
                    }
                    case IDENTIFICATION_SOURCE.DL: {
                        return 'volcano';
                    }
                }
            }
        }
        const onSearch = (values: any) => {
            fetchPortfolioWiseCustomerSummary(values.fundId)
        }
        const onSearchCustomers = (values: any) => {
            let filterDto = filter;
            filterDto.cin = checkUndefined(values.cin)
            filterDto.customerName = checkUndefined(values.customerName)
            filterDto.identificationValue = checkUndefined(values.identificationValue)
            filterDto.identificationSource = checkUndefined(values.identificationSource)

            fetchRMWiseCustomers(1, 10, filterDto)
        }
        const onReset = () => {
            fetchPortfolioWiseCustomerSummary(null)
        }

        const onResetCustomer = () => {
            let filterDto = filter;
            filterDto.cin = null
            filterDto.customerName = null
            filterDto.identificationValue = null
            filterDto.identificationSource = null
            fetchRMWiseCustomers(1, 10, filterDto)
        }
        const checkUndefined = (value: any) => {
            return value == undefined || value == "" ? null : value
        }
        const items: TabsProps['items'] = [
            {
                key: '1',
                label: 'Customer Details',
                children: <>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Collapse>
                                <Panel header="Filter Customers" key="1">
                                    <Form layout={"vertical"} onFinish={onSearchCustomers}>
                                        <Row gutter={[16, 16]}>

                                            <Col span={6}>
                                                <Form.Item label="Customer name" name="customerName">
                                                    <Input onKeyPress={Validation.onlyAlphabeticWithSpaceAndNumbers}
                                                           placeholder="Enter customer name"/>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item label="CIN" name="cin">
                                                    <Input onKeyPress={Validation.alphanumericAndNumbers}
                                                           placeholder="Enter CIN"/>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item label="Identification Type" name="identificationSource">
                                                    <Select
                                                        placeholder={'Select Identification Source'}
                                                        getPopupContainer={(trigger: {
                                                            parentNode: any;
                                                        }) => trigger.parentNode}
                                                        style={{width: '100%'}}
                                                        options={identificationSourceTypeList}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item label="Identification Value" name="identificationValue">
                                                    <Input onKeyPress={Validation.alphanumericAndNumbers}
                                                           placeholder="Enter identification value"/>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={[16, 0]}>
                                            <Col span={24}>
                                                <div className="tr-filter-btns">
                                                    <Button className={'tr-reset-btn'} icon={<ReloadOutlined/>}
                                                            htmlType={'reset'} onClick={onResetCustomer}>
                                                        Reset
                                                    </Button>
                                                    <Button className={'tr-search-btn'} type="primary"
                                                            htmlType={'submit'}
                                                            icon={<SearchOutlined/>}>
                                                        Search
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Panel>
                            </Collapse>
                        </Col>
                        <Col span={24} style={{marginTop: 8}}>
                            <Table scroll={{x: true}} dataSource={rmWiseCustomerList} pagination={false}
                                   columns={customerDetailsColumns}
                                   loading={isCustomerTableLoading}/>

                            <br/>
                            <Space style={{float: 'right'}}>
                                <Pagination
                                    total={pagination.total ?? 0}
                                    showTotal={(total: number) => `Total ${total} items`}
                                    pageSize={pagination.pageSize}
                                    defaultCurrent={pagination.current}
                                    current={pagination.current}
                                    onChange={handlePaginationChange}
                                />
                            </Space>
                        </Col>
                    </Row>
                </>,
            },
            {
                key: '2',
                label: 'Portfolio Wise Customers',
                children: <>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Collapse>
                                <Panel header="Filter Portfolio Wise Customers" key="1">
                                    <Form layout={"vertical"} onFinish={onSearch}>
                                        <Row gutter={[16, 16]}>

                                            <Col span={8}>
                                                <Form.Item
                                                    label="Fund" name={"fundId"}>
                                                    <Select placeholder={"Select Fund"}
                                                            options={fundDropdownList}
                                                    />
                                                </Form.Item>
                                            </Col>

                                        </Row>
                                        <Row gutter={[16, 0]}>
                                            <Col span={24}>
                                                <div className="tr-filter-btns">
                                                    <Button className={'tr-reset-btn'} icon={<ReloadOutlined/>}
                                                            htmlType={'reset'} onClick={onReset}>
                                                        Reset
                                                    </Button>
                                                    <Button className={'tr-search-btn'} type="primary"
                                                            htmlType={'submit'}
                                                            icon={<SearchOutlined/>}>
                                                        Search
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Panel>
                            </Collapse>
                        </Col>
                        <Col span={24} style={{marginTop: 8}}>
                            <Table scroll={{x: true}} dataSource={portfolioWiseCustomerList}
                                   columns={portfolioWiseCustomerColumns}
                                   loading={isPortfolioWiseTableLoading}/>
                        </Col>
                    </Row>
                </>,
            },
        ];

        const fetchCustomerPortfolioNumbers = (customerId: number) => {
            setPortfolioNumbersLoading(true)
            customerService.fetchCustomerWisePortfolios(customerId).then((res: any) => {
                if (res.data !== null && res.data.results.length > 0) {
                    let results = res.data.results;

                    setPortfolioNumberList(results)
                    form.setFieldsValue({portfolioId: results[0]})
                    setPortfolioNumber(results[0].value.portfolioId)
                    setPortfolioNumbersLoading(false)
                    setAccountNumber(extractFirstPart(results[0].label))
                } else {
                    setPortfolioNumberList([])
                    form.setFieldsValue({portfolioId: undefined})
                    setPortfolioNumbersLoading(false)
                }
            }).catch((reason) => {
                ErrorHandler.handleError(reason);
            });
        }

        const handleTabChange = (key: string) => {
            if ("2" === key) {
                fetchPortfolioWiseCustomerSummary(null)
            } else {
                fetchRMWiseCustomers(pagination.current, pagination.pageSize, filter)
            }
        };

        const getAddressDetails = (selectedCustomer: CustomerDto) => {
            let addressDto = {} as AddressDto;
            if (selectedCustomer.addressList && selectedCustomer.addressList.length > 0) {
                if (selectedCustomer.addressList?.length > 1) {
                    addressDto = selectedCustomer.addressList[1] as AddressDto
                } else {
                    addressDto = selectedCustomer.addressList[0] as AddressDto
                }
                return (
                    <>
                        <Divider orientationMargin="0" orientation="left"
                                 className='customer-detail-divider'>Address Details</Divider>
                        <Row gutter={[16, 16]} className={'template_request_statistic'}>
                            <Col span={12}>
                                <Statistic title="Line 1"
                                           value={CommonFunction.validateText(addressDto.line1)}/>
                            </Col>
                            <Col span={12}>
                                <Statistic title="Line 2"
                                           value={CommonFunction.validateText(addressDto.line2)}/>
                            </Col>
                            <Col span={12}>
                                <Statistic title="Country"
                                           value={CommonFunction.validateText(addressDto.countryName)}/>
                            </Col>
                            <Col span={12}>
                                <Statistic title="District"
                                           value={CommonFunction.validateText(CommonFunction.getDistrictLabelFromValue(addressDto.district))}/>
                            </Col>
                            <Col span={12}>
                                <Statistic title="Town"
                                           value={CommonFunction.validateText(addressDto.townOrCity)}/>
                            </Col>
                            <Col span={12}>
                                <Statistic title="Zip Code" formatter={(value: string) => value.replace(',', '')}
                                           value={CommonFunction.validateText(addressDto.postalCode)}/>
                            </Col>
                        </Row>
                    </>

                )
            }

        }

        const onPortfolioIdChange = (value: any, data: DropdownListDto) => {
            setAccountNumber(extractFirstPart(data.label))
            setPortfolioNumber(String(value))
        }

        const goBack = () => {
            history.push("/workflow/admin/manager-dashboard");
        }

        return (
            <div className={'rm-dashboard-tbl-filters'}>
                {!isRelationshipManagerUser() ?
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'>
                            <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'
                                 style={{paddingLeft: 0}}>
                                <h2 className='page-header-main-title'>
                                    <div className='header_icon header-icon-center'>
                                        <FontAwesomeIcon icon={faUserTie}/>
                                    </div>
                                    Details of Relationship Manager
                                </h2>
                            </Col>
                        </Col>
                        <FloatButton tooltip={<div>Go Back</div>}
                                     onClick={goBack}
                                     icon={<FontAwesomeIcon icon={faCircleLeft} style={{color: "#008d86",}}/>}
                                     shape="square"
                        />
                    </Row>

                    : <></>}

                <Row gutter={[16, 16]}>
                    <Col span={24} className={'main-card-bg-none'}>
                        <Spin size='large' spinning={isSummaryLoading}>
                            <Card className={'total-wealth-card'}>
                                <Row gutter={[16, 16]} style={{marginTop: 6, marginBottom: 6}}>
                                    <Col span={6} className={'total-wealth-card-border-right'}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-1-icon">
                                                <FontAwesomeIcon icon={faUsers}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>Total Clients</h5>
                                                <h4>
                                                    {CommonFunction.addThousandSeparators(relationshipManagerSummary?.numberOfClients)}
                                                </h4>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6} className={'total-wealth-card-border-right'}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-2-icon">
                                                <FontAwesomeIcon icon={faSackDollar}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>Total AUM</h5>
                                                <h4><span
                                                    className="lkr-span">LKR</span> {null == relationshipManagerSummary?.aum ? "0.00" : CommonFunction.decimalFormat(String(relationshipManagerSummary?.aum))}
                                                </h4>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6} className={'total-wealth-card-border-right'}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-3-icon">
                                                <FontAwesomeIcon icon={faMoneyBillTrendUp}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>Y2D Net Cash Flow</h5>
                                                <h4><span
                                                    className="lkr-span">LKR</span> {null == relationshipManagerSummary?.t2DCashFlow ? "0.00" : CommonFunction.decimalFormat(String(relationshipManagerSummary?.t2DCashFlow))}
                                                </h4>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-4-icon">
                                                <FontAwesomeIcon icon={faUser}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>Y2D New Accounts</h5>
                                                <h4>{CommonFunction.addThousandSeparators(relationshipManagerSummary?.t2DNewAccounts)}</h4>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </Spin>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{marginTop: 32}}>
                    <Col span={24}>
                        <Card className={'tab-card-custom-padding dashboard-card-box-shadow'}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'>
                                    <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'
                                         style={{paddingLeft: 0}}>
                                        <h2 className='page-header-main-title'>
                                            <div className='header_icon header-icon-center'>
                                                <FontAwesomeIcon icon={faUsers}/>
                                            </div>
                                            List of Customers
                                        </h2>
                                    </Col>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Tabs defaultActiveKey="1" items={items} onChange={handleTabChange}/>
                                </Col>
                            </Row>

                        </Card>
                    </Col>
                </Row>

                <Drawer title="Customer Details" onClose={onClose} open={open} size={'large'}
                        destroyOnClose={true}
                        className={'notification-center-drawer customer-details-drawer rm-dashboard-customer-drawer'}>
                    <Row gutter={[16, 24]}>
                        <Col span={24}>
                            <Card size={'small'} className={'drawer-card-box-shadow'} title={'Customer Details'}
                                  style={{marginTop: 16}}>
                                <Row gutter={[16, 16]} className={'template_request_statistic'}>
                                    <Col span={12}>
                                        <Statistic title="Customer Name"
                                                   value={CommonFunction.validateText(selectedCustomer?.nameToAppearOnStatement)}/>
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="CIN" formatter={(value: string) => value.replace(',', '')}
                                                   value={CommonFunction.validateText(selectedCustomer?.cin)}/>
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="Identification Type"
                                                   formatter={() => {
                                                       return (<Tag
                                                           color={getColor(selectedCustomer?.identificationSource?.sourceType)}>{CommonFunction.validateText(selectedCustomer?.identificationSource?.sourceType)}</Tag>)
                                                   }}/>
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="Identification Value"
                                                   formatter={(value: string) => value.replace(',', '')}
                                                   value={CommonFunction.validateText(selectedCustomer?.identificationValue)}/>
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="Mobile No" formatter={(value: string) => value.replace(',', '')}
                                                   value={CommonFunction.validateText(selectedCustomer?.mobileNo)}/>
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="Email"
                                                   value={CommonFunction.validateText(selectedCustomer?.email)}/>
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title="Date of Birth"
                                                   value={CommonFunction.validateText(selectedCustomer?.dateOfBirth)}/>
                                    </Col>

                                </Row>
                                {selectedCustomer && getAddressDetails(selectedCustomer)}
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card size={'small'} className={'drawer-card-box-shadow'} title={'Transaction Details'}
                                  extra={<a href={reportsUrl} target={"_blank"}>View Reports</a>}>
                                <Spin spinning={isPortfolioNumbersLoading}>
                                    <Form layout={"vertical"} className='form-input' form={form}>

                                        <Form.Item
                                            label=""
                                            name={"portfolioId"}
                                            style={{marginBottom: 20}}>
                                            <Select
                                                size={'large'}
                                                style={{width: '100%'}}
                                                placeholder="Select a portfolio"
                                                optionFilterProp="children"
                                                options={portfolioNumberList?.map((portfolio) => ({
                                                    value: portfolio.value.portfolioId,
                                                    label: portfolio.label,
                                                }))}
                                                onChange={onPortfolioIdChange}
                                            />
                                        </Form.Item>
                                    </Form>
                                    <RecentTransaction portfolioId={portfolioNumber as string}
                                                       accountNumber={accountNumber}
                                                       cinNumber={selectedCustomer?.cin as string}/>
                                </Spin>
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card size={'small'} className={'drawer-card-box-shadow'} title={'Pending Change Requests'}>
                                <PendingChangeRequest cinNumber={selectedCustomer?.cin as string}/>
                            </Card>
                        </Col>
                    </Row>
                </Drawer>
            </div>
        );
    }
;

export default RMDashboardPage;